import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  type = 'website',
  noindex = false,
  canonicalUrl = null // Allow custom canonical URL
}) => {
  const location = useLocation();
  const baseUrl = 'https://bookmycamera.com';
  
  // Normalize URL: remove trailing slash, remove query params for canonical
  const normalizePath = (pathname) => {
    // Remove trailing slash except for root
    let normalized = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    return normalized;
  };
  
  const normalizedPath = normalizePath(location.pathname);
  const currentUrl = canonicalUrl || `${baseUrl}${normalizedPath}`;
  const defaultImage = `${baseUrl}/assets/logos/BMC.png`;

  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update description
    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update Open Graph tags
    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }
    
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:image', image || defaultImage, true);
    updateMetaTag('twitter:image', image || defaultImage);
    updateMetaTag('twitter:url', currentUrl);

    // Update canonical URL - always use normalized URL without query params
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    // Use canonicalUrl if provided, otherwise use normalized path
    const canonicalHref = canonicalUrl || `${baseUrl}${normalizedPath}`;
    canonical.setAttribute('href', canonicalHref);
    
    // Also add alternate link for mobile (if needed)
    let alternate = document.querySelector('link[rel="alternate"][media="only screen and (max-width: 640px)"]');
    if (!alternate) {
      alternate = document.createElement('link');
      alternate.setAttribute('rel', 'alternate');
      alternate.setAttribute('media', 'only screen and (max-width: 640px)');
      document.head.appendChild(alternate);
    }
    alternate.setAttribute('href', canonicalHref);

    // Update robots meta
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Add structured data for current page
    const addBreadcrumbSchema = () => {
      // Remove existing breadcrumb schema if any
      const existingSchema = document.querySelector('script[data-breadcrumb-schema]');
      if (existingSchema) {
        existingSchema.remove();
      }

      const pathSegments = location.pathname.split('/').filter(Boolean);
      const breadcrumbItems = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        }
      ];

      let currentPath = '';
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        breadcrumbItems.push({
          "@type": "ListItem",
          "position": index + 2,
          "name": name,
          "item": `${baseUrl}${currentPath}`
        });
      });

      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-breadcrumb-schema', 'true');
      script.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(script);
    };

    addBreadcrumbSchema();

    // Cleanup function
    return () => {
      // Optionally clean up on unmount
    };
  }, [title, description, keywords, image, type, noindex, currentUrl, canonicalUrl, location.pathname, defaultImage, baseUrl, normalizedPath]);

  return null;
};

export default SEO;

